import React, { useEffect, useCallback, useState } from 'react';
import SearchableSelect from '../ui/SearchableSelect';
import { BasicInfo, MasterData } from '../../types';
import { useAuthContext } from '../../contexts/AuthContext';
import FamilySplitConfig from './FamilySplitConfig';

interface Props {
  basicInfo: BasicInfo;
  perc: number;
  additional: number;
  currency: string;
  tripType?: string;
  guestType?: string;
  mup?: number;
  masterData: MasterData;
  onChange: (updates: any) => void;
}

const BasicInfoForm: React.FC<Props> = ({ basicInfo, perc, additional, currency, tripType = 'FIT', guestType = 'FIT', mup = 450, masterData, onChange }) => {
  const { user } = useAuthContext();
  const tripTypes = ['FIT', 'Land Only', 'Group (10+)', 'Kerala New Agency', 'Outside Kerala'];
  const guestTypes = ['FIT', 'GROUP'];
  const currencies = ['INR', 'IDR', 'USD'];
  const [showFamilySplit, setShowFamilySplit] = useState(basicInfo.families && basicInfo.families.length > 0);

  const handleBasicInfoChange = useCallback((updates: Partial<BasicInfo>) => {
    onChange({ basic_info: { ...basicInfo, ...updates } });
  }, [basicInfo, onChange]);
  
  // Auto-populate Created By from logged-in user (only for new itineraries)
  useEffect(() => {
    if (user?.email && !basicInfo.created_by) {
      const today = new Date().toISOString().split('T')[0];
      handleBasicInfoChange({ 
        created_by: user.email,
        created_date: today
      });
    }
  }, [user, basicInfo.created_by, handleBasicInfoChange]);

  const handleChildrenAgesChange = (index: number, age: number) => {
    const newAges = [...basicInfo.children_ages];
    newAges[index] = age;
    handleBasicInfoChange({ children_ages: newAges });
  };

  const handleNoOfChildrenChange = (count: number) => {
    const newAges = Array(count).fill(17);
    for (let i = 0; i < Math.min(count, basicInfo.children_ages.length); i++) {
      newAges[i] = basicInfo.children_ages[i];
    }
    handleBasicInfoChange({ no_of_children: count, children_ages: newAges });
  };

  const handleTripTypeChange = (tripType: string) => {
    onChange({ trip_type: tripType });
  };

  const handleGuestTypeChange = (guestType: string) => {
    const updates: any = { guest_type: guestType };
    if (guestType === 'GROUP') {
      // Set default MUP based on pax count
      const totalPax = basicInfo.no_of_pax + basicInfo.no_of_children;
      updates.mup = totalPax >= 25 ? 350 : 450;
    }
    onChange(updates);
  };

  // Auto-calculate totals from families
  const hasFamilies = basicInfo.families && basicInfo.families.length > 0;
  const calculatedPax = hasFamilies ? (basicInfo.families || []).reduce((sum, f) => sum + f.no_of_pax, 0) : basicInfo.no_of_pax;
  const calculatedKids = hasFamilies ? (basicInfo.families || []).reduce((sum, f) => sum + f.no_of_kids, 0) : basicInfo.no_of_children;
  const calculatedAges = hasFamilies ? (basicInfo.families || []).flatMap(f => f.kids_ages) : basicInfo.children_ages;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agency <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={masterData.agencies && typeof masterData.agencies === 'object' ? Object.entries(masterData.agencies).map(([name, id]) => {
                const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                return idValue !== null ? { value: idValue, label: name } : null;
              }).filter((opt): opt is { value: number; label: string } => opt !== null) : []}
              value={basicInfo.agency_id !== -1 ? basicInfo.agency_id : ''}
              onChange={(value) => {
                const selectedOption = masterData.agencies && typeof masterData.agencies === 'object' ? 
                  Object.entries(masterData.agencies).find(([name, id]) => {
                    const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                    return idValue === value;
                  }) : null;
                
                if (selectedOption) {
                  handleBasicInfoChange({
                    agency_id: value as number,
                    agency_name: selectedOption[0]
                  });
                } else {
                  handleBasicInfoChange({
                    agency_id: -1,
                    agency_name: ''
                  });
                }
              }}
              placeholder="SELECT AGENCY"
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Pax <span className="text-red-500">*</span>
            </label>
            {hasFamilies ? (
              <div className="input-field bg-gray-100 flex items-center justify-between">
                <span>{calculatedPax}</span>
                <span className="text-xs text-gray-500">(from families)</span>
              </div>
            ) : (
              <input
                type="number"
                min="1"
                value={basicInfo.no_of_pax}
                onChange={(e) => handleBasicInfoChange({ no_of_pax: parseInt(e.target.value) || 1 })}
                className="input-field"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agency FOC
            </label>
            <input
              type="number"
              min="0"
              value={basicInfo.agency_foc_count || 0}
              onChange={(e) => handleBasicInfoChange({ agency_foc_count: parseInt(e.target.value) || 0 })}
              className="input-field"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">Agency representatives traveling free</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Kids
            </label>
            {hasFamilies ? (
              <div className="input-field bg-gray-100 flex items-center justify-between">
                <span>{calculatedKids}</span>
                <span className="text-xs text-gray-500">(from families)</span>
              </div>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                value={basicInfo.no_of_children === 0 ? '' : basicInfo.no_of_children}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === '0') {
                    handleNoOfChildrenChange(0);
                  } else {
                    const num = parseInt(val);
                    if (!isNaN(num) && num >= 0) {
                      handleNoOfChildrenChange(num);
                    }
                  }
                }}
                placeholder="0"
                className="input-field"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Type <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={masterData.vehicles && typeof masterData.vehicles === 'object' ? Object.entries(masterData.vehicles).map(([name, id]) => {
                const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                return idValue !== null ? { value: idValue, label: name } : null;
              }).filter((opt): opt is { value: number; label: string } => opt !== null) : []}
              value={basicInfo.vehicle_type_id !== -1 ? basicInfo.vehicle_type_id : ''}
              onChange={(value) => {
                const selectedOption = masterData.vehicles && typeof masterData.vehicles === 'object' ? 
                  Object.entries(masterData.vehicles).find(([name, id]) => {
                    const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                    return idValue === value;
                  }) : null;
                
                if (selectedOption) {
                  handleBasicInfoChange({
                    vehicle_type_id: value as number,
                    vehicle_type: selectedOption[0]
                  });
                } else {
                  handleBasicInfoChange({
                    vehicle_type_id: -1,
                    vehicle_type: ''
                  });
                }
              }}
              placeholder="SELECT VEHICLE TYPE"
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Guest Type
            </label>
            <select
              value={guestType}
              onChange={(e) => handleGuestTypeChange(e.target.value)}
              className="input-field"
            >
              {guestTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {guestType === 'FIT' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trip Type
              </label>
              <select
                value={tripType}
                onChange={(e) => handleTripTypeChange(e.target.value)}
                className="input-field"
              >
                {tripTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                MUP (Markup Per Person)
              </label>
              <input
                type="number"
                min="0"
                step="50"
                value={mup}
                onChange={(e) => onChange({ mup: parseFloat(e.target.value) || 450 })}
                className="input-field"
                placeholder="450"
              />
              <p className="text-xs text-gray-500 mt-1">
                Default: 350 (25+ pax), 450 (15-24 pax)
              </p>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Itinerary Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={basicInfo.itinerary_name}
              onChange={(e) => handleBasicInfoChange({ itinerary_name: e.target.value })}
              className="input-field"
              placeholder="Enter itinerary name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lead Guest Name
            </label>
            <input
              type="text"
              value={basicInfo.lead_guest}
              onChange={(e) => handleBasicInfoChange({ lead_guest: e.target.value })}
              className="input-field"
              placeholder="Enter guest name (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Travel Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={basicInfo.travel_date}
              onChange={(e) => handleBasicInfoChange({ travel_date: e.target.value })}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Days <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="4"
              value={basicInfo.no_of_days}
              onChange={(e) => handleBasicInfoChange({ no_of_days: parseInt(e.target.value) || 4 })}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              value={currency}
              onChange={(e) => onChange({ itinerary_currency: e.target.value })}
              className="input-field"
            >
              {currencies.map(curr => (
                <option key={curr} value={curr}>{curr}</option>
              ))}
            </select>
          </div>


        </div>
      </div>

      {/* Children Ages - Full Row */}
      {!hasFamilies && basicInfo.no_of_children > 0 && (
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Children Ages</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: basicInfo.no_of_children }).map((_, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Child {index + 1} Age
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={basicInfo.children_ages[index] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      const newAges = [...basicInfo.children_ages];
                      newAges[index] = 0;
                      handleBasicInfoChange({ children_ages: newAges });
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num) && num >= 0 && num <= 18) {
                        handleChildrenAgesChange(index, num);
                      }
                    }
                  }}
                  placeholder="Age"
                  className="input-field"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Family Split Configuration */}
      {(basicInfo.no_of_pax > 0 || basicInfo.no_of_children > 0) && (
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="familySplit"
              checked={showFamilySplit}
              onChange={(e) => {
                setShowFamilySplit(e.target.checked);
                if (!e.target.checked) {
                  handleBasicInfoChange({ families: undefined });
                }
              }}
              className="w-4 h-4 text-blue-600"
            />
            <label htmlFor="familySplit" className="text-sm font-medium text-gray-700 cursor-pointer">
              Split by families (for accommodation cost splitting)
            </label>
          </div>
          
          {showFamilySplit && (
            <FamilySplitConfig
              families={basicInfo.families || []}
              totalPax={basicInfo.no_of_pax}
              totalChildren={basicInfo.no_of_children}
              totalChildrenAges={basicInfo.children_ages}
              onChange={(families) => {
                // Auto-sync totals from families
                const totalPax = families.reduce((sum, f) => sum + f.no_of_pax, 0);
                const totalKids = families.reduce((sum, f) => sum + f.no_of_kids, 0);
                const allKidsAges = families.flatMap(f => f.kids_ages);
                handleBasicInfoChange({ 
                  families, 
                  no_of_pax: totalPax,
                  no_of_children: totalKids,
                  children_ages: allKidsAges
                });
              }}
            />
          )}
        </div>
      )}

      {/* Metadata Info Bar - Bottom */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <span className="text-gray-600 font-medium">Created By:</span>
            <p className="text-gray-900 mt-0.5">{basicInfo.created_by || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 font-medium">Created Date:</span>
            <p className="text-gray-900 mt-0.5">{basicInfo.created_date ? new Date(basicInfo.created_date).toLocaleDateString() : 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 font-medium">Last Updated By:</span>
            <p className="text-gray-900 mt-0.5">{basicInfo.last_updated_by || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 font-medium">Last Updated:</span>
            <p className="text-gray-900 mt-0.5">{basicInfo.last_updated_date ? new Date(basicInfo.last_updated_date).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BasicInfoForm;
